import { useState, useEffect, useCallback } from 'react';
import { chatApi, ChatSessionListItem } from '../services/api';

// Module-level cache — session lists are shared across Ask RCM, Library, and project pickers.
let _cache: ChatSessionListItem[] | null = null;
let _pending: Promise<ChatSessionListItem[]> | null = null;
let _revision = 0;
const _revisionListeners = new Set<(rev: number) => void>();

function bumpRevision() {
  _revision += 1;
  _revisionListeners.forEach((listener) => listener(_revision));
}

export function invalidateChatSessionsCache() {
  _cache = null;
  _pending = null;
}

async function fetchSessions(): Promise<ChatSessionListItem[]> {
  if (_cache) return _cache;
  if (!_pending) {
    _pending = chatApi.getSessions();
  }
  try {
    const data = await _pending;
    _cache = data;
    return data;
  } finally {
    _pending = null;
  }
}

export async function refreshChatSessionsCache(): Promise<ChatSessionListItem[]> {
  invalidateChatSessionsCache();
  const data = await fetchSessions();
  bumpRevision();
  return data;
}

export function useChatSessions() {
  const [revision, setRevision] = useState(_revision);
  const [sessions, setSessions] = useState<ChatSessionListItem[]>(_cache ?? []);
  const [loading, setLoading] = useState(_cache === null);

  useEffect(() => {
    const listener = (rev: number) => setRevision(rev);
    _revisionListeners.add(listener);
    return () => {
      _revisionListeners.delete(listener);
    };
  }, []);

  const refresh = useCallback(async () => {
    invalidateChatSessionsCache();
    setLoading(true);
    try {
      const data = await refreshChatSessionsCache();
      setSessions(data);
    } catch (error) {
      console.error('Error loading chat sessions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const invalidate = useCallback(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;

    if (_cache) {
      setSessions(_cache);
      setLoading(false);
      return;
    }

    setLoading(true);
    void fetchSessions()
      .then((data) => {
        if (!cancelled) {
          setSessions(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [revision]);

  return { sessions, loading, refresh, invalidate };
}
